import { jsPDF } from "jspdf";
import { formatTaskDisplayName } from "../../TeamTasks/utils/taskDisplayName";
import "jspdf-autotable";
import { BRANDS } from "../../../config/brand";

const BRAND = [25, 118, 210];
const MUTED = [100, 116, 139];
const INK = [15, 23, 42];

function safeSlug(value) {
  return String(value || "usuario")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48)
    .toLowerCase();
}

function formatHourLabel(hour) {
  const n = Number(hour);
  if (!Number.isInteger(n) || n < 0 || n > 23) return "—";
  return `${String(n).padStart(2, "0")}:00`;
}

function peakHourText(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const max = Math.max(0, ...list.map((row) => Number(row.value) || 0));
  if (!max) return "—";
  const hours = list.filter((row) => Number(row.value) === max).map((row) => formatHourLabel(row.hour));
  return `${hours.join(", ")} (${max})`;
}

function formatEffortSec(sec) {
  const n = Math.max(0, Math.round(Number(sec) || 0));
  if (!n) return "0m";
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (h > 0) return `${h}h${m ? ` ${m}m` : ""}`;
  return `${m || 1}m`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

async function loadLogoDataUrl() {
  const logoPath = BRANDS.xpro?.assets?.logo?.dark || "/brands/xpro/logowhite.png";
  try {
    const res = await fetch(logoPath);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function addFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    doc.setDrawColor(226, 232, 240);
    doc.line(14, h - 12, w - 14, h - 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text("Xpro · Relatório de tarefas executadas · Confidencial", 14, h - 7);
    doc.text(`${i} / ${pageCount}`, w - 14, h - 7, { align: "right" });
  }
}

function sectionTitle(doc, title, y) {
  const pageH = doc.internal.pageSize.getHeight();
  let cursor = y;
  if (cursor > pageH - 40) {
    doc.addPage();
    cursor = 18;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...INK);
  doc.text(title, 14, cursor);
  doc.setDrawColor(...BRAND);
  doc.setLineWidth(0.6);
  doc.line(14, cursor + 2, 72, cursor + 2);
  return cursor + 8;
}

/**
 * PDF com papel timbrado Xpro: tarefas executadas no período.
 * @param {{ user: object, period: {start:string,end:string}, teamTasks: object, fileName?: string }} args
 */
export async function exportOperatorTasksReportPdf({ user, period, teamTasks, fileName } = {}) {
  const tasks = Array.isArray(teamTasks?.tasks) ? teamTasks.tasks : [];
  const name = user?.nome || `Usuário #${user?.id || ""}`;
  const start = period?.start || "—";
  const end = period?.end || "—";
  const generatedAt = new Date().toLocaleString("pt-BR");
  const logoDataUrl = await loadLogoDataUrl();

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  // Papel timbrado: barra superior + logo
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageW, 36, "F");

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", 14, 8, 28, 12);
    } catch {
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Xpro", 14, 16);
    }
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Xpro", 14, 16);
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Relatório de tarefas executadas", 14, 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Período ${start} a ${end}`, pageW - 14, 16, { align: "right" });

  let y = 46;
  doc.setTextColor(...INK);
  doc.setFontSize(9);
  doc.text(`Colaborador: ${name}`, 14, y);
  y += 5;
  if (user?.email) {
    doc.setTextColor(...MUTED);
    doc.text(String(user.email), 14, y);
    y += 5;
  }
  if (user?.cargoNome) {
    doc.setTextColor(...MUTED);
    doc.text(`Cargo: ${user.cargoNome}`, 14, y);
    y += 5;
  }
  doc.setTextColor(...MUTED);
  doc.setFontSize(8);
  doc.text(`Gerado em ${generatedAt}`, 14, y);
  y += 8;

  y = sectionTitle(doc, "1. Resumo do período", y);
  doc.autoTable({
    startY: y,
    head: [["Indicador", "Valor"]],
    body: [
      ["Tarefas no período", String(teamTasks?.summary?.total ?? 0)],
      ["A fazer", String(teamTasks?.summary?.aFazer ?? 0)],
      ["Em andamento", String(teamTasks?.summary?.emAndamento ?? 0)],
      ["Para depois", String(teamTasks?.summary?.paraDepois ?? 0)],
      ["Concluídas", String(teamTasks?.summary?.concluidas ?? 0)],
      ["Atrasadas no período", String(teamTasks?.summary?.atrasadas ?? 0)],
      ["Atrasadas agora", String(teamTasks?.summary?.atrasadasAgora ?? 0)],
      [
        "No prazo",
        teamTasks?.summary?.onTimePct == null ? "—" : `${teamTasks.summary.onTimePct}%`,
      ],
      [
        "Tempo médio de conclusão",
        teamTasks?.summary?.avgCycleSec == null ? "—" : formatEffortSec(teamTasks.summary.avgCycleSec),
      ],
      ["Horário em que mais recebe", peakHourText(teamTasks?.receivedByHour)],
      ["Horário em que mais conclui", peakHourText(teamTasks?.completedByHour)],
      ["Tarefas executadas", String(teamTasks?.tasksCompleted ?? tasks.length)],
      ["Esforço total (tempo necessário)", formatEffortSec(teamTasks?.totalEffortSec)],
      ["Tempo médio de esforço / tarefa", formatEffortSec(teamTasks?.avgEffortSec)],
      [
        "Com tempo cadastrado",
        `${Number(teamTasks?.tasksWithDuration || 0)} de ${Number(teamTasks?.tasksCompleted ?? tasks.length)}`,
      ],
    ],
    theme: "striped",
    styles: { fontSize: 8.5, cellPadding: 2.2, textColor: INK },
    headStyles: { fillColor: BRAND, textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 90, fontStyle: "bold" },
      1: { cellWidth: "auto" },
    },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 10;

  y = sectionTitle(doc, "2. Tarefas executadas", y);
  if (!tasks.length) {
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("Nenhuma tarefa executada neste período.", 14, y);
  } else {
    doc.autoTable({
      startY: y,
      head: [["Tarefa", "Painel", "Concluída em", "Conclusão", "Aguardando", "Reaberturas"]],
      body: tasks.map((task) => [
        formatTaskDisplayName(task.id, task.title) || "—",
        task.boardName || "—",
        formatDateTime(task.completedAt),
        task.cycleSec != null ? formatEffortSec(task.cycleSec) : "—",
        Number(task.waitingSec) > 0 ? formatEffortSec(task.waitingSec) : "—",
        Number(task.reopenCount) > 0 ? String(task.reopenCount) : "—",
      ]),
      theme: "striped",
      styles: {
        fontSize: 8,
        cellPadding: 2,
        textColor: INK,
        overflow: "linebreak",
      },
      headStyles: { fillColor: BRAND, textColor: 255, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 52 },
        1: { cellWidth: 28 },
        2: { cellWidth: 30 },
        3: { cellWidth: 22, halign: "right" },
        4: { cellWidth: 24, halign: "right" },
        5: { cellWidth: 22, halign: "right" },
      },
      margin: { left: 14, right: 14 },
    });
  }

  addFooter(doc);

  const safeName =
    fileName ||
    `xpro-tarefas-${String(start).replace(/[^\d-]/g, "")}-${String(end).replace(/[^\d-]/g, "")}-${safeSlug(name)}`;
  doc.save(`${safeName}.pdf`);
}
